// src/push/index.js  →  dist/push.js  (global: Push)
//
// #4 slice 2 — the browser "turn on notifications" flow, shared so the managers
// app (now) and the staff app (slice 4) reuse one implementation. Asks for
// permission, subscribes via the service worker + the server's VAPID public key,
// and registers the device at /api/push/subscribe (the Worker tags it by role/
// area from the Bearer token). iOS needs the PWA on the Home Screen first, so we
// guide instead of failing silently.

function urlB64ToUint8Array(base64) {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

// iPhone/iPad in a normal Safari tab can't receive web push — the app must be
// added to the Home Screen and opened from there (iOS 16.4+).
export function isIosNeedsInstall() {
  try {
    const ua = navigator.userAgent || '';
    const ios = /iphone|ipad|ipod/i.test(ua) || (/Mac/.test(ua) && navigator.maxTouchPoints > 1);
    const standalone = (window.navigator && window.navigator.standalone === true) ||
      (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches);
    return ios && !standalone;
  } catch (e) { return false; }
}

function supported() {
  return ('serviceWorker' in navigator) && ('PushManager' in window) && (typeof Notification !== 'undefined');
}

// v125.88 — which PWA is this bundle running in? The standalone ELC Chat app
// (chat.html) declares itself so its subscription rows are tagged app='chat'
// and chat pushes get badged with the ELC Chat icon on the phone (Matt: chat
// notifications "are showing from the admin app, not the chat app"). Everything
// else returns null and the Worker keeps its 'managers' default.
// v131.41: the staff enrolment proves a PIN (the store is the staff row's).
function pushAuthFields() {
  try { return (typeof DB !== 'undefined' && DB.authFieldsOrNull && DB.authFieldsOrNull()) || {}; } catch (e) { return {}; }
}

function pushApp() {
  try { return /chat\.html$/i.test(window.location.pathname) ? 'chat' : null; } catch (e) { return null; }
}

// Race a promise against a timeout so a stalled step (serviceWorker.ready,
// pushManager.subscribe) can never hang the enrol flow forever. On timeout the
// returned promise rejects with `message`, which the caller's try/catch turns
// into a plain alert naming the failing step — so we learn WHICH await stalled.
function withTimeout(promise, ms, message) {
  let timer;
  const timeout = new Promise((_, reject) => { timer = setTimeout(() => reject(new Error(message)), ms); });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

// ── v125.79 — silent re-registration (Matt: "I need all notifications to be
// more permanent. i feel like I'm constantly reenabling.") ────────────────────
// Browsers rotate/expire push subscriptions, iOS sheds them under storage
// pressure, and an earlier server bug (22P02, fixed v125.61) lost saved rows —
// any of which read as "notifications turned themselves off". If permission is
// ALREADY granted, every app open now quietly re-subscribes and re-registers
// this device (idempotent upsert server-side), so tapping Enable is a one-time
// act per device. No prompts, no alerts — a failure just retries next open.
export async function refreshPush() {
  try {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    if (!supported() || isIosNeedsInstall()) return;
    const base = (typeof DB !== 'undefined' && DB.workerUrl) ? DB.workerUrl() : '';
    const token = (typeof DB !== 'undefined' && DB.getToken) ? DB.getToken() : null;
    const store = (typeof currentStore !== 'undefined' && currentStore) ||
      (typeof window !== 'undefined' && window.currentStore) || '';
    if (!token && !store) return;                        // nobody to register as yet
    let cfg = null;
    try {
      cfg = token
        ? await fetch(base + '/api/admin/push-config', { cache: 'no-store', headers: { 'Authorization': 'Bearer ' + token }, signal: AbortSignal.timeout(15000) }).then((r) => r.json())
        : await fetch(base + '/api/push/public-key', { cache: 'no-store', signal: AbortSignal.timeout(15000) }).then((r) => r.json());
    } catch (e) { return; }
    if (!cfg || !cfg.publicKey) return;
    const reg = await navigator.serviceWorker.ready;
    // subscribe() with the same key returns the EXISTING subscription when one
    // is live, or mints a fresh one when the browser rotated it — either way
    // the re-register below keeps the server row current.
    const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlB64ToUint8Array(cfg.publicKey) });
    if (token) {
      await fetch(base + '/api/push/subscribe', { cache: 'no-store',
        method: 'POST', headers: { 'Authorization': 'Bearer ' + token, 'Content-Type': 'application/json' },
        body: JSON.stringify({ subscription: sub.toJSON(), app: pushApp() }), signal: AbortSignal.timeout(15000),
      });
    } else {
      const pa = pushAuthFields();
      if (!Object.keys(pa).length) return;   // v131.41: nobody has verified a PIN yet - the next open retries
      await fetch(base + '/api/push/subscribe-staff', { cache: 'no-store',
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(Object.assign({ subscription: sub.toJSON(), store: store }, pa)), signal: AbortSignal.timeout(15000),
      });
    }
  } catch (e) { /* silent — the next app open retries */ }
}

// Self-arm on every page that carries this bundle (staff app, admin, /chat) —
// delayed so login/session restore has landed before we look for an identity.
if (typeof window !== 'undefined') {
  try { setTimeout(function () { refreshPush(); }, 5000); } catch (e) { /* no-op */ }
}

// Enable notifications for the managers app (admin Bearer). btn is optional (for
// the button's disabled/label state).
export async function enableManagers(btn) {
  const token = (typeof DB !== 'undefined' && DB.getToken) ? DB.getToken() : null;
  const base = (typeof DB !== 'undefined' && DB.workerUrl) ? DB.workerUrl() : '';
  const restore = btn ? btn.textContent : '';
  const done = (msg) => { if (typeof alert === 'function') alert((typeof I18N !== 'undefined' && I18N.t) ? I18N.t(msg) : msg); };
  try {
    if (isIosNeedsInstall()) {
      done('On iPhone or iPad: tap the Share button, choose "Add to Home Screen", open the app from there, then turn on notifications.');
      return;
    }
    if (!supported()) { done('This device or browser does not support notifications.'); return; }
    if (btn) { btn.disabled = true; }
    const perm = await Notification.requestPermission();
    if (perm !== 'granted') { done('Notifications were not allowed. Turn them on for this app in your browser settings, then try again.'); return; }
    if (btn) { btn.textContent = 'Enabling…'; }
    let cfg;
    try {
      cfg = await fetch(base + '/api/admin/push-config', { cache: 'no-store', headers: { 'Authorization': 'Bearer ' + token }, signal: AbortSignal.timeout(15000) }).then((r) => r.json());
    } catch (e) { done('Timed out contacting the server — check your connection and try again.'); return; }
    if (!cfg || !cfg.publicKey) { done('Notifications are not configured on the server yet. Ask Head Office.'); return; }
    const reg = await withTimeout(navigator.serviceWorker.ready, 15000, 'Timed out waiting for the app to be ready — fully close the app and reopen it from the Home Screen, then try again.');
    const sub = await withTimeout(reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlB64ToUint8Array(cfg.publicKey) }), 15000, 'Timed out subscribing to notifications — close and reopen the app from the Home Screen and try again.');
    let res;
    try {
      res = await fetch(base + '/api/push/subscribe', { cache: 'no-store',
        method: 'POST',
        headers: { 'Authorization': 'Bearer ' + token, 'Content-Type': 'application/json' },
        body: JSON.stringify({ subscription: sub.toJSON(), app: pushApp() }),
        signal: AbortSignal.timeout(15000),
      }).then((r) => r.json());
    } catch (e) { done('Timed out saving the subscription — try again.'); return; }
    // v125.50 (Matt hit this on prod): the server names WHY it refused (auth,
    // missing table/migration…) — show that instead of a blind "try again".
    done(res && res.ok ? 'Notifications are on for this device.'
      : 'Could not save the subscription on the server' + (res && res.error ? ': ' + res.error : '') + '. Please try again.');
  } catch (e) {
    done('Could not turn on notifications: ' + ((e && e.message) || e));
  } finally {
    if (btn) { btn.disabled = false; btn.textContent = restore || '🔔 Notifications'; }
  }
}

// Enable notifications for the staff app (tokenless). Tags the device to the
// signed-in STORE (read from the `currentStore` global), so the store gets its
// own alerts. Uses the PUBLIC vapid-key endpoint (no admin token in the till).
export async function enableStaff(btn) {
  const base = (typeof DB !== 'undefined' && DB.workerUrl) ? DB.workerUrl() : '';
  const store = (typeof currentStore !== 'undefined' && currentStore) ||
    (typeof window !== 'undefined' && window.currentStore) || '';
  const restore = btn ? btn.textContent : '';
  const done = (msg) => { if (typeof alert === 'function') alert((typeof I18N !== 'undefined' && I18N.t) ? I18N.t(msg) : msg); };
  try {
    if (isIosNeedsInstall()) {
      done('On iPhone or iPad: tap the Share button, choose "Add to Home Screen", open the app from there, then turn on notifications.');
      return;
    }
    if (!supported()) { done('This device or browser does not support notifications.'); return; }
    if (!store) { done('Please sign in to your store first, then turn on notifications.'); return; }
    const pa = pushAuthFields();   // v131.41
    if (!Object.keys(pa).length) { done('Clock in first (verify your PIN), then turn on notifications.'); return; }
    if (btn) { btn.disabled = true; }
    const perm = await Notification.requestPermission();
    if (perm !== 'granted') { done('Notifications were not allowed. Turn them on for this app in your browser settings, then try again.'); return; }
    if (btn) { btn.textContent = 'Enabling…'; }
    let cfg = null;
    try {
      cfg = await fetch(base + '/api/push/public-key', { cache: 'no-store', signal: AbortSignal.timeout(15000) }).then((r) => r.json());
    } catch (e) {
      if (e && e.name === 'TimeoutError') { done('Timed out contacting the server — check your connection and try again.'); return; }
      cfg = null;
    }
    if (!cfg || !cfg.publicKey) { done('Notifications are not set up yet. Please ask Head Office.'); return; }
    const reg = await withTimeout(navigator.serviceWorker.ready, 15000, 'Timed out waiting for the app to be ready — fully close the app and reopen it from the Home Screen, then try again.');
    const sub = await withTimeout(reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlB64ToUint8Array(cfg.publicKey) }), 15000, 'Timed out subscribing to notifications — close and reopen the app from the Home Screen and try again.');
    let res;
    try {
      res = await fetch(base + '/api/push/subscribe-staff', { cache: 'no-store',
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(Object.assign({ subscription: sub.toJSON(), store: store }, pa)),
        signal: AbortSignal.timeout(15000),
      }).then((r) => r.json());
    } catch (e) { done('Timed out saving the subscription — try again.'); return; }
    done(res && res.ok ? 'Notifications are on for this device.'
      : 'Could not save the subscription' + (res && res.error ? ': ' + res.error : '') + '. Please try again.');
  } catch (e) {
    done('Could not turn on notifications: ' + ((e && e.message) || e));
  } finally {
    if (btn) { btn.disabled = false; btn.textContent = restore || '🔔 Turn on notifications'; }
  }
}
